import classNames from 'classnames';
import { forwardRef, MouseEventHandler, PropsWithChildren } from 'react';

import styles from './icon-button.module.scss';
import { IconButtonClassName, IconButtonColor, IconButtonSize } from './icon-button.types';

const ICON_BUTTON_COLOR_LOOKUP: { [key in IconButtonColor]: IconButtonClassName } = {
  default:
    'bg-transparent disabled:bg-transparent text-gray-400 hover:bg-black/[0.04] active:bg-brand-primary active:bg-opacity-10 active:text-white dark:text-white dark:hover:bg-white/[0.08] dark:active:bg-cyan-700 dark:active:bg-opacity-25',
  primary:
    'bg-transparent disabled:bg-transparent text-brand-primary hover:bg-black/[0.04] active:bg-brand-primary active:bg-opacity-10 dark:text-brand-primary active:bg-opacity-5 dark:hover:bg-white/[0.08] dark:active:bg-brand-primary dark:active:bg-opacity-25',
  dark: 'bg-transparent disabled:bg-transparent text-zinc-300 hover:bg-white/[0.08] active:bg-white/[0.16]',
  none: undefined,
};

const ICON_BUTTON_SIZE_LOOKUP: { [key in IconButtonSize]: IconButtonClassName } = {
  sm: 'p-1',
  md: 'p-3',
  lg: 'p-4',
  none: undefined,
};

const ICON_BUTTON_FONT_SIZE_LOOKUP: { [key in IconButtonSize]: IconButtonClassName } = {
  sm: 'text-lg',
  md: 'text-2xl',
  lg: 'text-3xl',
  none: undefined,
};

/**
 * Icon specific button which should only have a icon as a child
 */
export interface IconButtonProps
  extends React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> {
  className?: IconButtonClassName;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  color?: IconButtonColor;
  size?: IconButtonSize;
  fontSize?: IconButtonSize;
  rounding?: 'default' | 'none';
}

export const IconButton = forwardRef<HTMLButtonElement, PropsWithChildren<IconButtonProps>>((props, ref) => {
  const {
    children,
    className,
    color = 'default',
    size = 'md',
    fontSize = size,
    rounding = 'default',
    ...otherProps
  } = props;
  return (
    <button
      type={'button'}
      className={classNames(
        styles['icon-button'],
        'transition ease-in-out duration-150',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary',
        'my-0 relative align-middle text-center inline-flex flex-grow-0 flex-shrink-0 justify-center items-center transition-colors',
        'disabled:cursor-[inherit]',
        color != null ? ICON_BUTTON_COLOR_LOOKUP[color] : undefined,
        size != null ? ICON_BUTTON_SIZE_LOOKUP[size] : undefined,
        fontSize != null ? ICON_BUTTON_FONT_SIZE_LOOKUP[fontSize] : undefined,
        rounding === 'default' && 'rounded-full',
        className,
      )}
      {...otherProps}
    >
      {children}
    </button>
  );
});

export default IconButton;
